import { CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { Toast as RadixToast } from 'radix-ui'
import { create } from 'zustand'

import { cn } from '../cn'

export type ToastTone = 'info' | 'success' | 'error'

export interface ToastMessage {
  readonly id: string
  readonly tone: ToastTone
  readonly title: string
  readonly description?: string | undefined
}

interface ToastState {
  readonly toasts: readonly ToastMessage[]
  push: (toast: Omit<ToastMessage, 'id'>) => string
  dismiss: (id: string) => void
}

const MAX_VISIBLE = 3

/** Store, not context: mutations (non-React code) need to toast too. */
export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  push: (toast) => {
    const id = crypto.randomUUID()
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }].slice(-MAX_VISIBLE) }))
    return id
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },
}))

export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'success', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'info', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'error', title, description }),
}

const TONE: Record<ToastTone, { bar: string; icon: typeof Info }> = {
  info: { bar: 'border-l-accent', icon: Info },
  success: { bar: 'border-l-recovered', icon: CheckCircle2 },
  error: { bar: 'border-l-lost', icon: XCircle },
}

/** Mount once near the app root. Bottom-right, dark, left accent bar — the prototype's `.toast`. */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts)
  const dismiss = useToastStore((state) => state.dismiss)
  return (
    <RadixToast.Provider swipeDirection="right" duration={6000}>
      {toasts.map((item) => {
        const Icon = TONE[item.tone].icon
        return (
          <RadixToast.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) dismiss(item.id)
            }}
            className={cn(
              'flex min-w-[260px] items-start gap-2.5 rounded-[9px] border-l-[3px] bg-ink px-4 py-3 text-[12.5px] text-white shadow-lg data-[state=open]:animate-[fade-in_.2s_ease]',
              TONE[item.tone].bar,
            )}
          >
            <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <RadixToast.Title className="font-semibold">{item.title}</RadixToast.Title>
              {item.description !== undefined && (
                <RadixToast.Description className="mt-0.5 text-white/75">
                  {item.description}
                </RadixToast.Description>
              )}
            </div>
            <RadixToast.Close
              aria-label="Dismiss"
              className="rounded p-0.5 text-white/70 hover:text-white"
            >
              <X className="size-3.5" />
            </RadixToast.Close>
          </RadixToast.Root>
        )
      })}
      <RadixToast.Viewport className="fixed right-4 bottom-4 z-[60] flex w-[min(380px,calc(100vw-32px))] flex-col gap-2 outline-none" />
    </RadixToast.Provider>
  )
}
